/**
 * Booking a call, in one place.
 *
 * The Calendly address was copied into three separate email builders (the audit report and
 * two lead-magnet follow-ups) and appeared nowhere on the site itself, so the only way to
 * reach a call was to have already been sent an email. Everything that offers a call now
 * points at `/book`, and `/book` is the only thing that knows the Calendly address.
 */

/**
 * The scheduling page itself. Changing the event type means changing this line only.
 * The event was renamed from /clientmeeting to /strategycall in Calendly, which left the old
 * address a 404 and the /book page showing nothing (found 2026-10-07).
 */
export const CALENDLY_URL = "https://calendly.com/ownlydigital/strategycall";

/** Where anything on the site sends someone who wants a call. Never Calendly directly. */
export const OWNLY_BOOKING_HREF = "/book";

/**
 * Where someone lands once the call is booked.
 *
 * Two routes lead here. The calendar embedded on /book sends the page a
 * `calendly.event_scheduled` message and BookingFlow moves the visitor on itself, so the
 * embed needs no Calendly setting at all. Somebody who booked on Calendly's own page (the
 * "book straight from our booking page" link, or an old emailed link) only arrives here if
 * the event type's "After booking" option redirects to https://www.ownlydigital.co.uk/booked.
 * Leave Calendly's "pass event details" box UNTICKED: it would put the invitee's name and
 * email in the address bar, and Vercel Analytics records the full address.
 */
export const BOOKED_PATH = "/booked";

/**
 * How long the call actually runs.
 *
 * THIRTY, and it has always been thirty. The Calendly event is "Strategy Call, 30 min",
 * confirmed by loading the live embed on 2026-08-31. The audit email and both lead-magnet
 * follow-ups had been offering "a fifteen minute call" against it for months, so everyone
 * who booked one arrived expecting half the meeting they had actually taken. Nobody caught
 * it because the length was typed into the copy and never read back off the booking page.
 *
 * If the event type is ever changed in Calendly, change this line and the wording follows.
 */
export const BOOKING_MINUTES = 30;

/**
 * THE ONE CALL TO ACTION, worded the same everywhere (Tom's decision, 2026-09-23).
 *
 * The funnel used to alternate between "Book a call", "Book a strategy call" and "Talk to us",
 * and promised "the person who would build it". Ownly is being set up to scale, so the review is
 * with Ownly, run by whichever consultant is assigned, and the wording never promises a specific
 * person. `BOOKING_CTA` is the button; `BOOKING_CTA_FULL` is the sentence form for headings and
 * emails. Both follow `BOOKING_MINUTES`.
 */
export const BOOKING_CTA = `Book a ${BOOKING_MINUTES}-minute review`;
export const BOOKING_CTA_FULL = `${BOOKING_CTA} with Ownly`;

/** Supporting lines that sit under the CTA. Company voice, no named person. */
export const BOOKING_SUPPORT_HEADLINE = "Talk through the problem with Ownly.";
export const BOOKING_SUPPORT_BODY =
  "We'll look at how the work happens now, where it is breaking down and what needs to change. You do not need a specification or a finished idea.";

/**
 * Where /book leaves a note for /booked about the booking that just happened.
 *
 * sessionStorage, not the address bar: the Calendly event and invitee references are opaque ids,
 * but anything in a URL reaches Vercel Analytics and browser history. This is strictly necessary
 * storage for a service the visitor asked for (showing them the booking they just made), lives
 * only as long as the tab, and holds no name or email.
 */
export const BOOKED_STORAGE_KEY = "ownly-booked";

/** The `utm_term` value that tells Lead Engine the booker accepted ad cookies. Keep in step with
    META_ADS_CONSENT_MARKER in lead-engine's src/lib/meta-capi.ts. */
export const ADS_CONSENT_MARKER = "ads_consent_granted";

/** The uuid at the end of a Calendly API uri, or null when it is not one. */
export function calendlyUuidFrom(uri: unknown): string | null {
  if (typeof uri !== "string") return null;
  const match = /\/(?:scheduled_events|invitees)\/([0-9a-f-]{16,64})\/?$/i.exec(uri.trim());
  return match ? match[1].toLowerCase() : null;
}

/** Reads the two references out of Calendly's `calendly.event_scheduled` message. */
export function bookingReferenceFrom(data: unknown): { event: string; invitee: string | null } | null {
  if (!data || typeof data !== "object") return null;
  const payload = (data as { payload?: unknown }).payload;
  if (!payload || typeof payload !== "object") return null;
  const event = calendlyUuidFrom((payload as { event?: { uri?: unknown } }).event?.uri);
  if (!event) return null;
  const invitee = calendlyUuidFrom((payload as { invitee?: { uri?: unknown } }).invitee?.uri);
  return { event, invitee };
}

export type BookingPrefill = {
  name?: string | null;
  email?: string | null;
  /** Their number, which the event type asks for and makes required. */
  phone?: string | null;
  /** Free text carried into the "anything we should know" answer, e.g. their estimate. */
  note?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  /**
   * The /book answers, carried where the person cannot see or edit them.
   *
   * WHY. The answers used to ride ONLY in the notes box (`note`, a2). That box is editable, and
   * on 2026-09-23 a prospect answered both questions, then typed her own message over the box and
   * booked: every answer was gone before it left the page. Calendly passes `utm_content` straight
   * through to the booking (webhook `tracking.utm_content`) and never shows it, so the answers go
   * there too and Lead Engine reads this first. Calendly's own docs say custom values in UTM
   * fields are fine. Nothing on the site uses utm_content for campaigns, so nothing is displaced.
   */
  answers?: string | null;
  gclid?: string | null;
  msclkid?: string | null;
  fbclid?: string | null;
  /**
   * The visitor pressed Accept on the cookie banner.
   *
   * Carried in `utm_term`, which Calendly passes straight through to the webhook
   * (`tracking.utm_term`) and never shows, and which nothing else on the site uses. Lead Engine
   * sends its server-side copy of the booking to Meta ONLY when this marker is present, so a
   * visitor who rejected ad cookies is not reported to Meta by the back door either.
   */
  adsConsent?: boolean;
  /**
   * The host the frame sits on, e.g. "www.ownlydigital.co.uk". Embed only.
   *
   * WITHOUT THIS CALENDLY SENDS THE PAGE NOTHING. Verified 2026-09-23 by framing the live
   * event type in a test page: no `embed_domain` and not one postMessage arrived; with it,
   * `calendly.event_type_viewed` and friends arrive at once. Calendly's own widget script adds
   * it, and this site deliberately does not load that script, so the "booking-completed" and
   * "booking-time-selected" analytics events had never fired once since they were written.
   */
  embedDomain?: string | null;
};

/**
 * The URL for the inline iframe.
 *
 * An iframe and not Calendly's widget script on purpose. The script would need
 * `script-src` and `style-src` opened up to a third party on every page of this site to
 * save one CSS variable of work; a plain frame needs `frame-src` and nothing else, and it
 * cannot read anything on our origin. The cost is that the frame does not resize itself,
 * so the page gives it a fixed height that fits the month view at both widths.
 */
export function calendlyEmbedUrl(prefill: BookingPrefill = {}): string {
  const url = new URL(CALENDLY_URL);
  // Calendly's own consent banner inside our frame, on a page that already explains what
  // loading the frame does and only loads it when asked.
  url.searchParams.set("hide_gdpr_banner", "1");
  url.searchParams.set("background_color", "0f1525");
  url.searchParams.set("text_color", "ffffff");
  url.searchParams.set("primary_color", "2b7fff");
  if (prefill.name) url.searchParams.set("name", prefill.name);
  if (prefill.email) url.searchParams.set("email", prefill.email);
  /*
   * THE NUMBERS ARE POSITIONS, NOT NAMES, AND THEY WERE WRONG UNTIL 2026-09-02.
   *
   * `a1` is the FIRST custom question on the event type and `a2` the second. On the Strategy Call
   * they are, in order: "Please enter your phone number" (required, type phone_number) and
   * "Please share anything that will help prepare for our meeting" (optional, text). Every note we
   * have ever prefilled went into `a1`, which means the estimate summary and the audit finding
   * were being typed into the phone box of a required phone field.
   *
   * Read the live mapping back before changing these, never guess it:
   *   curl -H "Authorization: Bearer $CALENDLY_API_TOKEN" \
   *     https://api.calendly.com/event_types/bc04ff39-f5af-413c-87ad-1206de9771a0
   * and look at `custom_questions[].position`. Reordering the questions in Calendly moves them.
   *
   * A prefill Calendly has nowhere to put is ignored, so an extra one is harmless. A prefill in
   * the wrong box is not.
   */
  if (prefill.phone) url.searchParams.set("a1", prefill.phone);
  if (prefill.note) url.searchParams.set("a2", prefill.note);
  if (prefill.utmSource) url.searchParams.set("utm_source", prefill.utmSource);
  if (prefill.utmMedium) url.searchParams.set("utm_medium", prefill.utmMedium);
  if (prefill.utmCampaign) url.searchParams.set("utm_campaign", prefill.utmCampaign);
  if (prefill.answers) url.searchParams.set("utm_content", prefill.answers.slice(0, 250));
  if (prefill.gclid) url.searchParams.set("gclid", prefill.gclid);
  if (prefill.msclkid) url.searchParams.set("msclkid", prefill.msclkid);
  if (prefill.fbclid) url.searchParams.set("fbclid", prefill.fbclid);
  if (prefill.adsConsent) url.searchParams.set("utm_term", ADS_CONSENT_MARKER);
  if (prefill.embedDomain) {
    url.searchParams.set("embed_domain", prefill.embedDomain);
    url.searchParams.set("embed_type", "Inline");
  }
  return url.toString();
}

/** The hosted Calendly fallback, carrying attribution without embed-only styling flags. */
export function calendlyExternalUrl(prefill: BookingPrefill = {}): string {
  const embedded = new URL(calendlyEmbedUrl(prefill));
  for (const key of [
    "hide_gdpr_banner",
    "background_color",
    "text_color",
    "primary_color",
    "embed_domain",
    "embed_type",
  ]) {
    embedded.searchParams.delete(key);
  }
  return embedded.toString();
}

/** Accept only the documented event messages sent by Calendly's own embed. */
export function calendlyEventName(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const event = (data as { event?: unknown }).event;
  return typeof event === "string" && event.startsWith("calendly.") ? event : null;
}

/**
 * The same link for an email, absolute, because a relative href is dead in an inbox.
 *
 * Emails point at /book and never at Calendly directly. Three reasons: the click is ours to
 * count, the page explains the call before the calendar appears, and if the event type ever
 * moves there is one line to change instead of four email templates nobody remembers exist.
 */
export function bookingEmailUrl(siteUrl: string, prefill: BookingPrefill = {}): string {
  const base = siteUrl.replace(/\/$/, "");
  return `${base}${bookingHrefWith(prefill)}`;
}

/** `/book` with whatever we already know about them, so nobody retypes their own name. */
export function bookingHrefWith(prefill: BookingPrefill = {}): string {
  const params = new URLSearchParams();
  if (prefill.name) params.set("name", prefill.name);
  if (prefill.email) params.set("email", prefill.email);
  if (prefill.phone) params.set("phone", prefill.phone);
  if (prefill.note) params.set("note", prefill.note);
  const query = params.toString();
  return query ? `${OWNLY_BOOKING_HREF}?${query}` : OWNLY_BOOKING_HREF;
}
